import ProfileTabEmptyState from "@apps/profile/components/profile-tab-empty-state";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";

import useCareerRoleOptions from "../use-career-role-options";
import { newCareerTrackPath } from "./helpers";

type Person = { id: number; name: string };

// The admin's version: assign a role, or with no track to take one from,
// start one. Its own component so only admins load every role.
const AssignRoleEmptyState = ({
  organizationId,
  person,
  onRoleAssigned,
}: {
  organizationId: number;
  person: Person;
  onRoleAssigned?: () => void;
}) => {
  const roleOptions = useCareerRoleOptions(organizationId);
  const hasNoRolesToAssign =
    !roleOptions.loading && roleOptions.options.length === 0;

  if (hasNoRolesToAssign) {
    // Roles live on career tracks: with none to pick, the first step is
    // making a track, not assigning.
    return (
      <ProfileTabEmptyState
        title="No career tracks yet"
        body={`There aren't any roles to assign ${person.name} yet, because no career track has been created. Create a track first, then assign them one of its roles.`}
        action={
          <Button
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            text="Create Track"
            to={newCareerTrackPath(organizationId)}
          />
        }
      />
    );
  }

  return (
    <ProfileTabEmptyState
      title="No role yet"
      body={`${person.name} isn't on a career track. Assign them a role to see what's expected of them here.`}
      action={
        <ComboboxGeneric
          aria-label="Assign role menu"
          loading={roleOptions.loading}
          options={roleOptions.options}
          value={null}
          searchPlaceholder="Search roles..."
          // They hold no role yet, so there's none to end.
          onChangeValue={async (option) => {
            await roleOptions.assignRole(person.id, option.value);
            onRoleAssigned?.();
          }}
        >
          <ComboboxGeneric.Button
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            text="Assign Role"
            disabled={roleOptions.isSaving}
          />
        </ComboboxGeneric>
      }
    />
  );
};

// What someone without a role sees in place of their career track: on the
// Career Tracks overview and person page, and on the profile's Career tab.
// Whoever runs the ladder can assign one from here; everyone else is told
// who can.
const PersonRoleEmptyState = ({
  organizationId,
  person,
  canManageTracks,
  isSelf,
  onRoleAssigned,
}: {
  organizationId: number;
  person: Person;
  canManageTracks: boolean;
  isSelf: boolean;
  // For a page whose own query doesn't follow the assignment.
  onRoleAssigned?: () => void;
}) =>
  canManageTracks ? (
    <AssignRoleEmptyState
      organizationId={organizationId}
      person={person}
      onRoleAssigned={onRoleAssigned}
    />
  ) : (
    <ProfileTabEmptyState
      title="No role yet"
      body={
        isSelf
          ? "You aren't on a career track yet. Ask an administrator to assign you a role."
          : `${person.name} isn't on a career track yet. An administrator can assign them a role.`
      }
    />
  );

export default PersonRoleEmptyState;
